import React, { useMemo } from "react";
import { filter, map, orderBy, reduce, sumBy, toNumber, uniqBy } from "lodash";
import { cn } from "@/shared/utils/shadcn";
import Groups from "./Groups";
import styles from "./index.module.scss";
import Blob from "./Blob";
import Sectors from "./Sectors";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { AvatarWithInitials } from "@/shared/ui/Avatar";

export interface DimensionsChartProps {
  dimensions: DimensionWithScores[];
  defaultPersonId?: number;
  variant?: string;
  onClickSector: (sector: any, allSectors: any[]) => void;
}

type Coordinate = {
  x: number;
  y: number;
};

interface ScoreBlobs {
  default: Coordinate[];
  members?: {
    [id: string]: Coordinate[];
  };
}

const DimensionsChart: React.FC<DimensionsChartProps> = (props) => {
  const { dimensions = [], onClickSector, defaultPersonId } = props;

  const [activeMemberId, setActiveMemberId] = React.useState(defaultPersonId);

  const sectorDimensions = dimensions.filter((d) => !!d.parentId);

  const groups = useMemo(() => {
    const groups = dimensions.filter((d) => !d.parentId);

    return groups.map((group) => ({
      ...group,
      dimensions: dimensions.filter((d) => d.parentId === group.id),
    }));
  }, [dimensions]);

  const wedgeAngle = 360 / sectorDimensions.length;
  const midX = 50;
  const midY = 50;
  const sectorRadius = 30;

  const members = useMemo(
    () =>
      map(
        sectorDimensions?.[0]?.peopleScores,
        (personScore) => personScore.person
      ),
    [sectorDimensions]
  );

  const getScoreCoordinates = (score: number, index: number) => {
    if (score === 0 || !score) score = 1;
    const startAngle = wedgeAngle * index;
    const scoreAngle = startAngle + wedgeAngle / 2;

    let distance = (score * sectorRadius) / 100;
    if (distance === 0) distance = 0;
    const scorePosX = Math.cos((scoreAngle * Math.PI) / 180) * distance + 50;
    const scorePosY = Math.sin((scoreAngle * Math.PI) / 180) * distance + 50;

    return { x: scorePosX, y: scorePosY };
  };

  const scores = useMemo(
    () =>
      reduce(
        sectorDimensions,
        (acc, dimension, index) => {
          const { score, averageScore } = dimension;

          const scoreValue = averageScore?.value || score?.value;

          const defaultScoreCoordinates = getScoreCoordinates(
            toNumber(scoreValue),
            index
          );

          const peopleScores = defaultPersonId
            ? uniqBy(
                filter(
                  dimension.peopleScores,
                  (personScore) => defaultPersonId === personScore.person?.id
                ),
                "person.id"
              )
            : dimension.peopleScores;

          const membersScoreCoordinates = reduce(
            peopleScores,
            (mAcc, personScore) => {
              const personId = toNumber(personScore?.person?.id);
              return {
                ...mAcc,
                [personId]: [
                  ...(mAcc[personId] || []),
                  getScoreCoordinates(toNumber(personScore.value), index),
                ],
              };
            },
            acc.members || {}
          );

          return {
            default: [...acc.default, defaultScoreCoordinates],
            members: membersScoreCoordinates,
          };
        },
        { default: [], members: {} } as ScoreBlobs
      ),
    [sectorDimensions, defaultPersonId]
  );

  const activeMemberScores =
    (activeMemberId || defaultPersonId) &&
    scores.members?.[activeMemberId || defaultPersonId || ""];

  const getMemberPosition = (memberScores: Coordinate[]) => {
    const xSum = sumBy(memberScores, (coord) => coord.x);
    const ySum = sumBy(memberScores, (coord) => coord.y);
    const userCenterX = xSum / memberScores.length - midX / 2;
    const userCenterY = ySum / memberScores.length;

    return {
      x: userCenterX * 3 - 30,
      y: userCenterY - 2,
    };
  };

  const handleSectorClick = (sector: any) => {
    onClickSector(sector, dimensions);
  };

  const MemberAvatars = useMemo(() => {
    const membersScores = scores.members || {};
    const avatarSize = 24; // Adjust this value to change the size of the avatars

    if (Object.keys(membersScores).length <= 1 && !defaultPersonId) return null;

    const renderAvatar = (
      id: string,
      scores: Coordinate[],
      isActive: boolean
    ) => {
      const position = getMemberPosition(scores);
      const member = members.find((m) => m?.id === toNumber(id));
      if (!member) return null;

      return (
        <g
          key={id}
          className="cursor-pointer"
          transform={`translate(${position.x}, ${position.y}) scale(0.20,0.20)`}
        >
          <foreignObject
            stroke="white"
            x={0}
            y={0}
            width={avatarSize}
            height={avatarSize}
            className="overflow-visible"
            onMouseEnter={() => setActiveMemberId(toNumber(id))}
            onMouseLeave={() => setActiveMemberId(undefined)}
          >
            <div
              className={`w-full h-full fixed rounded-full overflow-hidden transition-transform duration-200 hover:scale-125 z-30 hover:z-50 ${
                isActive ? "scale-125 z-50" : ""
              }`}
            >
              <AvatarWithInitials
                avatar={member.avatar || ""}
                initials={member.initials || ""}
                className="w-full h-full object-cover border border-white border-solid"
              />
            </div>
          </foreignObject>
        </g>
      );
    };

    const avatars = orderBy(
      Object.entries(membersScores).map(([id, scores]) => ({
        id,
        scores,
        element: renderAvatar(id, scores, toNumber(id) === activeMemberId),
      })),
      (avatar) => toNumber(avatar.id) === activeMemberId
    );

    return avatars.map((avatar) => avatar.element);
  }, [scores, members, activeMemberId, defaultPersonId]);

  return (
    <div className="flex flex-1 w-full h-full max-h-[755px] relative">
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(styles.main)}
      >
        {!!activeMemberScores && activeMemberScores.length && (
          <Blob
            wedgeAngle={wedgeAngle}
            midX={midX}
            midY={midY}
            isMemberHover={!!activeMemberScores}
            sectorRadius={sectorRadius}
            scores={scores.default}
          />
        )}
        <Blob
          wedgeAngle={wedgeAngle}
          midX={midX}
          midY={midY}
          isMember={!!activeMemberScores}
          sectorRadius={sectorRadius}
          scores={
            (!!activeMemberScores && activeMemberScores) || scores.default
          }
        />
        <Sectors
          wedgeAngle={wedgeAngle}
          midX={midX}
          midY={midY}
          sectorRadius={sectorRadius}
          dimensions={sectorDimensions}
          onClick={handleSectorClick}
        />
        <Groups
          wedgeAngle={wedgeAngle}
          midX={midX}
          midY={midY}
          sectorRadius={sectorRadius}
          groups={groups}
          type={props.variant}
        />
        {MemberAvatars}
      </svg>
    </div>
  );
};

export { DimensionsChart };
